<template>
  <div class="request-invoice">
    <LoadingOverlayComponent v-if="isLoading" />
    <h4 class="font-serif mb-4">
      {{ $t("requestInvoice.submit_invoice") }}
    </h4>
    <div class="row text-center mb-3 align-middle g-0">
      <div class="col-12 col-md-3 align-self-center">
        <PendingPayoutFigure variant="summary" :parts="moneyPendingParts" />
        <p class="mb-0 fs-9 fw-bold">
          {{ $t("requestInvoice.money_pending") }}
        </p>
      </div>
      <div
        id="sum-explainer"
        class="card col-12 col-md-9 row bg-secondary-subtle p-2 g-0 mt-2 mt-md-0"
      >
        <div class="col-4">
          <p data-test="sales-total" class="mb-0">
            {{ formatCurrencyTotals(sales) }}
          </p>
          <p class="mb-0 fs-9">{{ $t("requestInvoice.sales_revenue") }}</p>
        </div>
        <div class="col-4">
          <p data-test="sales-vat-total" class="mb-0">
            {{ formatCurrencyTotals(vat) }}
          </p>
          <p class="mb-0 fs-9">
            {{ $t("requestInvoice.sales_vat") }}
          </p>
        </div>
        <div class="col-4">
          <p data-test="fees-total" class="mb-0">
            {{ formatCurrencyTotals(fees) }}
          </p>
          <p class="mb-0 fs-9">{{ $t("requestInvoice.service_fees_vat") }}</p>
        </div>
      </div>
    </div>
    <div class="card mt-sm-2 mb-4">
      <div class="card-body p-1 px-3 p-sm-2 px-sm-3 bg-body-highlight">
        <div
          class="d-flex align-items-center"
          @click="toggleCollapse"
          role="button"
        >
          <div class="avatar avatar-x1 me-2">
            <font-awesome-icon
              icon="rectangle-list"
              class="text-secondary-emphasis"
            />
          </div>
          <div class="flex-1 fw-bold fs-9">
            {{ $t("requestInvoice.detailed_overview") }}
          </div>
          <div class="btn btn-text p-2">
            {{ $t("requestInvoice.view_details") }}
          </div>
        </div>
        <div class="text-body-secondary mt-1" v-show="showCollapse">
          <hr />
          <template v-if="monthSplit && splitByMonth">
            <div v-for="group in monthGroups" :key="group.id" class="mb-4">
              <h5 class="fs-8 mb-3 d-flex justify-content-between">
                <span>{{ group.heading }}</span>
                <span data-test="month-total">{{
                  formatCurrencyTotals(group.totals)
                }}</span>
              </h5>
              <div class="fs-9 card p-2 p-lg-0 overflow-hidden">
                <InvoiceItemHeaderRowComponent />
                <InvoiceItemDisplayComponent
                  v-for="item in group.items"
                  :key="item.id"
                  :invoiceItem="item"
                />
              </div>
            </div>
          </template>
          <template v-else>
            <h5 class="fs-8 mb-3">{{ $t("requestInvoice.invoice_items") }}</h5>
            <div class="fs-9 mb-4 card p-2 p-lg-0 overflow-hidden">
              <InvoiceItemHeaderRowComponent />
              <InvoiceItemDisplayComponent
                v-for="item in pendingInvoiceItems.filter(
                  (item) => Number(item.unit_price) < 0
                )"
                :key="item.id"
                :invoiceItem="item"
              />
            </div>
            <div
              v-if="
                pendingInvoiceItems.filter(
                  (item) => Number(item.unit_price) > 0
                ).length > 0
              "
              class="mb-3"
            >
              <h5 class="fs-8 mb-3">
                {{ $t("requestInvoice.offset_service_fees") }}
              </h5>
              <div class="fs-9 mb-3 card p-2 p-lg-0 overflow-hidden">
                <InvoiceItemHeaderRowComponent />
                <InvoiceItemDisplayComponent
                  v-for="item in pendingInvoiceItems.filter(
                    (item) => Number(item.unit_price) > 0
                  )"
                  :key="item.id"
                  :invoiceItem="item"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <hr />

    <h4 class="fs-8 font-serif mb-3">
      {{ $t("requestInvoice.invoice_details") }}
    </h4>

    <form @submit.prevent="requestInvoice" novalidate>
      <div class="row">
        <div class="col-12 col-sm-6">
          <div class="mb-3">
            <label for="invoiceNumber" class="form-label fs-9 p-0 text-body">{{
              $t("requestInvoice.invoice_number")
            }}</label>
            <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': v$.localInvoiceNumber.$error }"
              id="invoiceNumber"
              v-model="localInvoiceNumber"
              @blur="v$.localInvoiceNumber.$touch"
              :placeholder="$t('requestInvoice.invoice_number_placeholder')"
              maxlength="35"
            />
            <div
              class="invalid-feedback"
              v-for="error of v$.localInvoiceNumber.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
          <div class="mb-3">
            <label for="reference" class="form-label fs-9 p-0 text-body">{{
              $t("requestInvoice.reference")
            }}</label>
            <input
              type="text"
              class="form-control"
              id="reference"
              v-model="localReference"
              :placeholder="$t('requestInvoice.reference_placeholder')"
              maxlength="35"
            />
          </div>
          <!-- No need to show VAT code field to Estonian private individuals -->
          <div
            class="mb-3"
            v-if="company.country != 'EE' || company.registry_code?.length == 8"
          >
            <label for="vat" class="form-label fs-9 p-0 text-body">{{
              $t("requestInvoice.vat_number")
            }}</label>
            <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': v$.localVat.$error }"
              id="vat"
              v-model="localVat"
              :placeholder="$t('requestInvoice.vat_number_placeholder')"
              @blur="v$.localVat.$touch"
            />
            <div
              class="invalid-feedback"
              v-for="error of v$.localVat.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
          <div class="mb-3">
            <label for="bankAccount" class="form-label fs-9 p-0 text-body">
              {{ $t("requestInvoice.bank_account_iban") }}
            </label>
            <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': v$.localBankAccount.$error }"
              id="bankAccount"
              v-model="localBankAccount"
              :placeholder="$t('requestInvoice.bank_account_placeholder')"
              @blur="v$.localBankAccount.$touch"
            />
            <div
              class="invalid-feedback"
              v-for="error of v$.localBankAccount.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="col-12 col-sm-6 mb-3">
          <label for="paymentHeader" class="form-label fs-9 p-0 text-body">{{
            $t("requestInvoice.contact_block_on_invoice")
          }}</label>
          <textarea
            class="form-control"
            :class="{ 'is-invalid': v$.localPaymentHeader.$error }"
            id="paymentHeader"
            v-model="localPaymentHeader"
            :maxlength="PAYMENT_HEADER_MAX_LENGTH"
            :placeholder="$t('requestInvoice.contact_block_placeholder')"
            @blur="v$.localPaymentHeader.$touch"
          ></textarea>
          <div
            class="invalid-feedback"
            v-for="error of v$.localPaymentHeader.$errors"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
        </div>
      </div>
      <div v-if="monthSplit" class="mb-3">
        <div class="form-check">
          <input
            type="checkbox"
            class="form-check-input"
            :id="splitByMonthId"
            data-test="split-by-month"
            v-model="splitByMonth"
          />
          <label
            class="form-check-label fs-9"
            :for="splitByMonthId"
            :title="monthSplitTooltip"
          >
            {{ $t("requestInvoice.month_split_label", { date: invoiceDate }) }}
          </label>
        </div>
        <div v-if="showSecondInvoiceNumber" class="mt-3 col-12 col-sm-6">
          <label
            :for="currentMonthInvoiceNumberId"
            class="form-label fs-9 p-0 text-body"
            >{{
              $t("requestInvoice.month_split_current_number", {
                current_month: currentMonthName,
              })
            }}</label
          >
          <input
            type="text"
            class="form-control"
            :class="{ 'is-invalid': v$.localCurrentMonthInvoiceNumber.$error }"
            :id="currentMonthInvoiceNumberId"
            data-test="current-month-invoice-number"
            v-model="localCurrentMonthInvoiceNumber"
            @blur="v$.localCurrentMonthInvoiceNumber.$touch"
            maxlength="35"
          />
          <div
            class="invalid-feedback"
            v-for="error of v$.localCurrentMonthInvoiceNumber.$errors"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
        </div>
      </div>
      <div class="form-check mb-3">
        <input
          type="checkbox"
          class="form-check-input"
          id="monthlyRollup"
          v-model="localMonthlyRollup"
        />
        <label class="form-check-label fs-9" for="monthlyRollup">
          {{ $t("requestInvoice.monthly_rollup_label") }}
        </label>
      </div>
      <button
        type="submit"
        class="btn btn-primary w-100 mb-3"
        :disabled="!canSubmit"
      >
        {{ $t("requestInvoice.submit_invoice") }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import InvoiceItemDisplayComponent from "@/components/common/InvoiceItemDisplayComponent.vue";
import InvoiceItemHeaderRowComponent from "@/money/invoice/InvoiceItemHeaderRowComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import {
  formatCurrencyTotals,
  formatDayMonth,
  formatMonthName,
} from "@/composables/formatUtils";
import {
  pendingPayoutFigureParts,
  pendingPayoutsByPayoutCurrency,
  pendingPayoutTotals,
} from "@/composables/money/invoiceUtils";
import { feesFilter, salesFilter } from "@/composables/money/moneyUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useValidators } from "@/composables/useValidators";
import { PAYMENT_HEADER_MAX_LENGTH } from "@/money/invoice/invoiceLimits";
import PendingPayoutFigure from "@/money/PendingPayoutFigure.vue";
import type {
  Company,
  InvoiceItem,
  MonthSplit,
  PendingPayoutByCompanyId,
} from "@/types";
import useVuelidate from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { filter } from "lodash-es";
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useServerDraft } from "@/composables/useServerDraft";

const props = withDefaults(
  defineProps<{
    company: Company;
    pendingInvoiceItems: InvoiceItem[];
    defaultInvoiceNumber?: string;
    monthSplit?: MonthSplit | null;
    pendingPayoutByCompanyId: PendingPayoutByCompanyId;
  }>(),
  { defaultInvoiceNumber: "", monthSplit: null }
);

const emit = defineEmits<{
  "invoice-requested": [];
}>();

const { t, locale } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();
const localVat = ref(props.company.vat);
const localBankAccount = ref(props.company.bank_account);
const localPaymentHeader = ref(props.company.payment_header);
const localInvoiceNumber = ref(props.defaultInvoiceNumber);
const localReference = ref("");
const localMonthlyRollup = ref(
  Boolean(props.company.monthly_notification_rollup)
);
const splitByMonth = ref(true);
const localCurrentMonthInvoiceNumber = useServerDraft(
  () => props.monthSplit?.default_current_month_invoice_number ?? "",
  (defaultNumber) => defaultNumber
);
const showCollapse = ref(false);
const isLoading = ref(false);
const splitByMonthId = useId();
const currentMonthInvoiceNumberId = useId();

// Per currency, like the figure beside it: amounts in different currencies are never added together.
const saleItems = computed(() =>
  filter(props.pendingInvoiceItems, salesFilter)
);

const sales = computed(() => pendingPayoutTotals(saleItems.value, "net_units"));

const vat = computed(() => pendingPayoutTotals(saleItems.value, "vat_units"));

const fees = computed(() =>
  pendingPayoutTotals(filter(props.pendingInvoiceItems, feesFilter))
);

const moneyPendingParts = computed(() =>
  pendingPayoutFigureParts(
    props.pendingInvoiceItems,
    props.company,
    props.pendingPayoutByCompanyId
  )
);

const canSubmit = computed(
  () =>
    pendingPayoutsByPayoutCurrency(
      props.pendingInvoiceItems,
      [props.company],
      props.pendingPayoutByCompanyId
    ).length > 0
);

const showSecondInvoiceNumber = computed(
  () => splitByMonth.value && Boolean(props.monthSplit?.creates_second_invoice)
);

const invoiceDate = computed(() =>
  props.monthSplit
    ? formatDayMonth(props.monthSplit.invoice_date, locale.value)
    : ""
);

const previousMonthName = computed(() =>
  props.monthSplit
    ? formatMonthName(props.monthSplit.invoice_date, locale.value)
    : ""
);

// The split date is the previous month's last day, so the next day falls in the current month
const currentMonthName = computed(() => {
  if (!props.monthSplit) {
    return "";
  }
  const nextDay = new Date(`${props.monthSplit.invoice_date}T00:00:00Z`);
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  return formatMonthName(nextDay.toISOString().slice(0, 10), locale.value);
});

const monthSplitTooltip = computed(() =>
  t(
    props.monthSplit?.creates_second_invoice
      ? "requestInvoice.month_split_tooltip"
      : "requestInvoice.month_split_tooltip_pending",
    {
      month: previousMonthName.value,
      date: invoiceDate.value,
      current_month: currentMonthName.value,
    }
  )
);

const monthGroups = computed(() => {
  if (!props.monthSplit) {
    return [];
  }
  const groupOf = (ids: number[], id: string, heading: string) => {
    const items = filter(props.pendingInvoiceItems, (item) =>
      ids.includes(item.id)
    );
    return {
      id,
      heading,
      items,
      totals: pendingPayoutTotals(items),
    };
  };
  const groups = [
    groupOf(
      props.monthSplit.previous_month_item_ids,
      "previous",
      t("requestInvoice.month_split_previous_heading", {
        month: previousMonthName.value,
        date: invoiceDate.value,
      })
    ),
    groupOf(
      props.monthSplit.current_month_item_ids,
      "current",
      t(
        props.monthSplit.creates_second_invoice
          ? "requestInvoice.month_split_current_heading"
          : "requestInvoice.month_split_pending_heading",
        { current_month: currentMonthName.value }
      )
    ),
  ];
  return filter(groups, (group) => group.items.length > 0);
});

const toggleCollapse = () => {
  showCollapse.value = !showCollapse.value;
};

// INPUT VALIDATION SECTION
const {
  required,
  maxLength,
  estonianVATNumber,
  latvianVATNumber,
  lithuanianVATNumber,
  iban,
} = useValidators(t);

const vat_check = computed(() => {
  if (props.company.country === "EE") {
    return estonianVATNumber;
  } else if (props.company.country === "LV") {
    return latvianVATNumber;
  } else if (props.company.country === "LT") {
    return lithuanianVATNumber;
  } else {
    return null;
  }
});

const differsFromFirstNumber = helpers.withMessage(
  () => t("requestInvoice.month_split_number_must_differ"),
  (value: string) => value.trim() !== localInvoiceNumber.value.trim()
);

const rules = computed(() => ({
  localInvoiceNumber: { required, maxLength: maxLength(35) },
  localVat: vat_check.value ? { vat_check: vat_check.value } : {},
  localBankAccount: { required, iban },
  localPaymentHeader: {
    required,
    maxLength: maxLength(PAYMENT_HEADER_MAX_LENGTH),
  },
  localCurrentMonthInvoiceNumber: showSecondInvoiceNumber.value
    ? { required, maxLength: maxLength(35), differsFromFirstNumber }
    : {},
}));

const v$ = useVuelidate(rules, {
  localInvoiceNumber,
  localVat,
  localBankAccount,
  localPaymentHeader,
  localCurrentMonthInvoiceNumber,
});

const checkValidity = () => {
  v$.value.$touch();
  return !v$.value.$error;
};

function monthSplitPayload() {
  if (!props.monthSplit) {
    return {};
  }
  return {
    split_by_month: splitByMonth.value,
    current_month_invoice_number: showSecondInvoiceNumber.value
      ? localCurrentMonthInvoiceNumber.value
      : null,
  };
}

async function requestInvoice() {
  if (!checkValidity()) {
    return;
  }
  isLoading.value = true;
  try {
    const response = await goSolidApi.post(
      `/api/company/${props.company.id}/generate-invoice`,
      {
        invoice_number: localInvoiceNumber.value,
        vat_number: localVat.value,
        bank_account: localBankAccount.value,
        payment_header: localPaymentHeader.value,
        reference: localReference.value || null,
        monthly_notification_rollup: localMonthlyRollup.value,
        ...monthSplitPayload(),
      }
    );
    if (response.status === 200) {
      emit("invoice-requested");
    } else {
      console.error("Error requesting invoice");
      throw new Error("Error requesting invoice");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
}
</script>

<style scoped lang="scss">
form {
  .form-label {
    text-transform: none;
  }
}
#paymentHeader {
  min-height: 10rem;
}

#sum-explainer {
  flex-direction: row;
}
</style>
